import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Percent, X, TrendingUp, TrendingDown } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';

const BulkPricePanel = ({ catOptions, onClose, onApplied }) => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [direction, setDirection] = useState('up');
  const [amount, setAmount] = useState('10');
  const [mode, setMode] = useState('all');
  const [cats, setCats] = useState([]);
  const [roundTo, setRoundTo] = useState('dollar');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgErr, setMsgErr] = useState(false);
  const say = (text, isErr = false) => { setMsg(text); setMsgErr(isErr); };

  const pct = (direction === 'down' ? -1 : 1) * (parseFloat(amount) || 0);
  const body = useMemo(() => ({ percent: pct, round_to: roundTo, category_mode: mode, category_ids: mode === 'all' ? [] : cats }), [pct, roundTo, mode, cats]);
  const invalid = !pct || (mode !== 'all' && cats.length === 0);

  useEffect(() => {
    if (invalid) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setPreview(null);
      try { setPreview((await axios.post(`${API}/admin/products/bulk-adjust-price`, { ...body, dry_run: true }, { headers })).data); if (msgErr) say(''); }
      catch (e) { say(e?.response?.data?.detail || 'Preview failed', true); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, invalid, headers]);

  const toggleCat = (id) => setCats(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const apply = async () => {
    if (!window.confirm(`${pct > 0 ? 'Increase' : 'Decrease'} ${preview?.would_update} product prices by ${Math.abs(pct)}%? This cannot be undone automatically.`)) return;
    setBusy(true); say('');
    try {
      const { data } = await axios.post(`${API}/admin/products/bulk-adjust-price`, body, { headers });
      say(`Updated ${data.modified} of ${data.matched} matched products.`);
      onApplied();
    } catch (e) { say('Failed: ' + (e?.response?.data?.detail || e.message), true); }
    finally { setBusy(false); }
  };

  const dirBtn = (d, Icon, label) => (
    <button data-testid={`bulk-direction-${d}`} onClick={() => setDirection(d)} className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] rounded-md border ${direction === d ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-medium' : 'border-neutral-200 dark:border-neutral-700 text-neutral-600'}`}><Icon size={14} /> {label}</button>
  );

  return (
    <div data-testid="bulk-price-panel" className="rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/40 dark:bg-emerald-950/10 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="inline-flex items-center gap-2 text-[15px] font-semibold text-neutral-900 dark:text-neutral-100"><Percent size={16} className="text-emerald-600" /> Bulk price update</h3>
        <button data-testid="bulk-close-btn" onClick={onClose} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={15} /></button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-2">{dirBtn('up', TrendingUp, 'Increase')}{dirBtn('down', TrendingDown, 'Decrease')}</div>
        <label className="block">
          <span className="block text-[10.5px] tracking-widest uppercase text-neutral-500 mb-1">Percent</span>
          <input data-testid="bulk-percent-input" type="number" min="0" step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls + ' w-28'} />
        </label>
        <label className="block">
          <span className="block text-[10.5px] tracking-widest uppercase text-neutral-500 mb-1">Rounding</span>
          <select data-testid="bulk-rounding-select" value={roundTo} onChange={(e) => setRoundTo(e.target.value)} className={inputCls}>
            <option value="dollar">Whole dollar</option>
            <option value="cent">Nearest cent</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-[10.5px] tracking-widest uppercase text-neutral-500 mb-1">Apply to</span>
          <select data-testid="bulk-mode-select" value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}>
            <option value="all">All products</option>
            <option value="include">Only selected categories</option>
            <option value="exclude">All except selected categories</option>
          </select>
        </label>
      </div>
      {mode !== 'all' && (
        <div className="flex flex-wrap gap-2" data-testid="bulk-category-list">
          {catOptions.map(c => (
            <button key={c.id} data-testid={`bulk-cat-${c.id}`} onClick={() => toggleCat(c.id)} className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${cats.includes(c.id) ? (mode === 'include' ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-rose-500 border-rose-500 text-white') : 'border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300'}`}>{c.label}</button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div data-testid="bulk-preview" className="text-[13px] text-neutral-600 dark:text-neutral-300">
          {invalid ? 'Enter a percent and pick categories to preview.' : (msgErr && !preview) ? 'Fix the error below to preview.' : preview ? <>Will update <b className="text-neutral-900 dark:text-neutral-100">{preview.would_update}</b> of {preview.total} products ({preview.skipped} unchanged) by <b className={pct > 0 ? 'text-emerald-600' : 'text-rose-500'}>{pct > 0 ? '+' : ''}{pct}%</b></> : 'Calculating…'}
        </div>
        <button data-testid="bulk-apply-btn" onClick={apply} disabled={busy || invalid || !preview?.would_update} className="btn-brand rounded-md px-5 py-2 text-[13.5px] font-semibold disabled:opacity-50">{busy ? 'Applying…' : 'Apply price change'}</button>
      </div>
      {msg && <div data-testid="bulk-msg" className={`text-[12.5px] ${msgErr ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{msg}</div>}
    </div>
  );
};

export default BulkPricePanel;
